import React, { useState, useEffect } from 'react';
import { Video, Play, Eye, EyeOff, Loader2, RefreshCw } from 'lucide-react';
import { useUserVideos } from '@/hooks/queries';
import { videoService } from '@/services/videoService';
import VideoPlayer from '@/components/VideoPlayer';
import PublicVideoPlayer from '@/components/PublicVideoPlayer';

// Create a global cache for video URLs
window.videoUrlCache = window.videoUrlCache || {};

export default function UserVideos() {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [activeVideoTab, setActiveVideoTab] = useState('public');
  const [urlLoadingStates, setUrlLoadingStates] = useState({});
  const [urlErrors, setUrlErrors] = useState({});
  const [videoReadyStates, setVideoReadyStates] = useState({});

  // Fetch user videos
  const { data: userVideosData, isLoading, error, refetch } = useUserVideos();
  
  // Extract videos from API response and transform them
  const rawVideos = userVideosData?.data || [];
  
  // Transform each video into separate public and private videos
  const allVideos = [];
  rawVideos.forEach(video => {
    // Add public version if public_url exists
    if (video.public_url) {
      allVideos.push({
        ...video,
        id: `${video.id}_public`,
        type: 'public',
        stream_url: video.public_url,
        title: `Public Version - ${video.filename?.split('/').pop() || 'Video'}`,
      });
    }
    
    // Add private version if private_url exists
    if (video.private_url) {
      allVideos.push({
        ...video,
        id: `${video.id}_private`,
        type: 'private',
        stream_url: video.private_url,
        title: `Private Version - ${video.filename?.split('/').pop() || 'Video'}`,
      });
    }
  });
  
  // Separate public and private videos
  const publicVideos = allVideos.filter(video => video.type === 'public');
  const privateVideos = allVideos.filter(video => video.type === 'private');

  // Reset video ready states when switching tabs
  useEffect(() => {
    setVideoReadyStates({});
  }, [activeVideoTab]);

  // Pre-fetch URLs for all videos when data changes
  useEffect(() => {
    const fetchVideoUrls = async () => {
      const videosToFetch = allVideos.filter(video => {
        const cacheKey = `${video.type}_${video.stream_url}`;
        return !window.videoUrlCache[cacheKey] && !urlLoadingStates[cacheKey];
      });

      if (videosToFetch.length === 0) return;

      // Set loading states
      const newLoadingStates = {};
      videosToFetch.forEach(video => {
        const cacheKey = `${video.type}_${video.stream_url}`;
        newLoadingStates[cacheKey] = true;
      });
      setUrlLoadingStates(prev => ({ ...prev, ...newLoadingStates }));

      // Fetch URLs in parallel
      const fetchPromises = videosToFetch.map(async (video) => {
        const cacheKey = `${video.type}_${video.stream_url}`;
        try {
          const url = await videoService.getPublicVideoStreamUrl(video.stream_url);
          window.videoUrlCache[cacheKey] = url;
          
          // Clear loading state for this video
          setUrlLoadingStates(prev => {
            const newState = { ...prev };
            delete newState[cacheKey];
            return newState;
          });

          // Clear any previous errors
          setUrlErrors(prev => {
            const newState = { ...prev };
            delete newState[cacheKey];
            return newState;
          });
        } catch (error) {
          console.error(`Error fetching URL for ${video.type} video:`, error);
          
          // Set error state
          setUrlErrors(prev => ({ ...prev, [cacheKey]: true }));
          
          // Clear loading state
          setUrlLoadingStates(prev => {
            const newState = { ...prev };
            delete newState[cacheKey];
            return newState;
          });
        }
      });

      await Promise.all(fetchPromises);
    };

    if (allVideos.length > 0) {
      fetchVideoUrls();
    }
  }, [allVideos.length, rawVideos]);

  // Function to get cached URL or loading/error state
  const getVideoUrlState = (video) => {
    const cacheKey = `${video.type}_${video.stream_url}`;
    return {
      url: window.videoUrlCache[cacheKey] || null,
      loading: !!urlLoadingStates[cacheKey],
      error: !!urlErrors[cacheKey]
    };
  };

  // Function to handle video ready state
  const handleVideoReady = (videoId) => {
    setVideoReadyStates(prev => ({ ...prev, [videoId]: true }));
  };

  // Function to retry URL fetching for a specific video
  const retryVideoUrl = async (video) => {
    const cacheKey = `${video.type}_${video.stream_url}`;
    
    setUrlLoadingStates(prev => ({ ...prev, [cacheKey]: true }));
    setUrlErrors(prev => {
      const newState = { ...prev };
      delete newState[cacheKey];
      return newState;
    });

    // Reset video ready state when retrying
    setVideoReadyStates(prev => {
      const newState = { ...prev };
      delete newState[video.id];
      return newState;
    });

    try {
      const url = await videoService.getPublicVideoStreamUrl(video.stream_url);
      window.videoUrlCache[cacheKey] = url;
      
      setUrlLoadingStates(prev => {
        const newState = { ...prev };
        delete newState[cacheKey];
        return newState;
      });
    } catch (error) {
      console.error(`Error retrying URL for ${video.type} video:`, error);
      setUrlErrors(prev => ({ ...prev, [cacheKey]: true }));
      setUrlLoadingStates(prev => {
        const newState = { ...prev };
        delete newState[cacheKey];
        return newState;
      });
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-foreground mb-2">My Videos</h2>
          <p className="text-muted-foreground">Loading your videos...</p>
        </div>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="animate-spin h-8 w-8 text-primary" />
        </div>
      </div>
    );
  }

  if (error) {
    // Handle genuine errors (404 "no videos" case is now handled in the query hook)
    return (
      <div className="space-y-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-foreground mb-2">My Videos</h2>
          <p className="text-muted-foreground">Error loading your videos</p>
        </div>
        <div className="text-center py-12 bg-background border border-secondary-border rounded-xl">
          <div className="text-red-500 mb-4">
            {error?.response?.data?.message || "Failed to load your videos"}
          </div>
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center mx-auto"
          >
            <RefreshCw className="mr-2" size={16} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  const renderVideoGrid = (videos, videoType) => {
    if (videos.length === 0) {
      return (
        <div className="text-center py-12 bg-background border border-secondary-border rounded-xl">
          <Video className="mx-auto h-16 w-16 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">
            No {videoType} Videos
          </h3>
          <p className="text-muted-foreground mb-4">
            {videoType === 'public' 
              ? 'Your approved public videos will appear here once processed by our team.'
              : 'Your private videos will appear here once uploaded and processed.'
            }
          </p>
          {rawVideos.length === 0 && (
            <button
              onClick={() => refetch()}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center mx-auto"
            >
              <RefreshCw className="mr-2" size={16} />
              Check Again
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="grid gap-6 grid-cols-1">
        {videos.map((video, index) => {
          const urlState = getVideoUrlState(video);
          
          return (
            <div 
              key={video.id || index} 
              className="bg-background border border-secondary-border rounded-lg overflow-hidden hover:shadow-md transition-all duration-200"
            >
              <div className="aspect-video bg-gray-900 overflow-hidden relative">
                {urlState.error ? (
                  <div className="w-full h-full flex items-center justify-center bg-secondary text-muted-foreground">
                    <div className="text-center">
                      <div className="text-xl mb-2">⚠️</div>
                      <p className="text-sm mb-2">Failed to load video</p>
                      <button 
                        onClick={() => retryVideoUrl(video)}
                        className="text-xs text-primary hover:text-primary/80 underline"
                      >
                        Retry
                      </button>
                    </div>
                  </div>
                ) : urlState.loading || !urlState.url ? (
                  <div className="w-full h-full flex items-center justify-center bg-black">
                    <div className="text-center text-white">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-2 mx-auto"></div>
                      <p className="text-sm">Loading video...</p>
                    </div>
                  </div>
                ) : (
                  <div className="relative w-full h-full">
                    <video
                      className={`w-full h-full object-contain ${!videoReadyStates[video.id] ? 'pointer-events-none' : ''}`}
                      controls
                      preload="metadata"
                      src={urlState.url}
                      crossOrigin="anonymous"
                      onCanPlay={() => handleVideoReady(video.id)}
                    >
                      Your browser does not support the video tag.
                    </video>
                    {!videoReadyStates[video.id] && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black z-10 pointer-events-auto">
                        <div className="text-center text-white">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-2 mx-auto"></div>
                          <p className="text-sm">Loading video...</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Video Type Badge */}
                <div className="absolute top-2 right-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    videoType === 'public' 
                      ? 'bg-primary/90 text-white' 
                      : 'bg-secondary text-foreground border border-secondary-border'
                  }`}>
                    {videoType === 'public' ? (
                      <>
                        <Eye className="mr-1" size={12} />
                        Public
                      </>
                    ) : (
                      <>
                        <EyeOff className="mr-1" size={12} />
                        Private
                      </>
                    )}
                  </span>
                </div>
              </div>
              
              <div className="p-4">
                <div className="mb-2">
                  <h4 className="font-medium text-foreground text-base mb-1">
                    {video.title}
                  </h4>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-foreground mb-2">My Videos</h2>
          <p className="text-muted-foreground">
            View your legacy videos. ({rawVideos.length} video{rawVideos.length !== 1 ? 's' : ''} with {allVideos.length} version{allVideos.length !== 1 ? 's' : ''})
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-secondary border border-secondary-border text-foreground rounded-lg hover:bg-secondary/90 transition-colors flex items-center"
        >
          <RefreshCw className="mr-2" size={16} />
          Refresh
        </button>
      </div>

      {/* Video Type Tabs */}
      <div className="mb-6">
        <div className="flex space-x-1 bg-secondary border border-secondary-border rounded-lg p-1">
          <button
            onClick={() => setActiveVideoTab('public')}
            className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeVideoTab === 'public'
                ? 'bg-primary text-white shadow-sm'
                : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
            }`}
          >
            <Eye className="h-4 w-4 mr-2" />
            Public Videos ({publicVideos.length})
          </button>
          <button
            onClick={() => setActiveVideoTab('private')}
            className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeVideoTab === 'private'
                ? 'bg-primary text-white shadow-sm'
                : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
            }`}
          >
            <EyeOff className="h-4 w-4 mr-2" />
            Private Videos ({privateVideos.length})
          </button>
        </div>
      </div>

      {/* Video Content */}
      {activeVideoTab === 'public' && renderVideoGrid(publicVideos, 'public')}
      {activeVideoTab === 'private' && renderVideoGrid(privateVideos, 'private')}
    </div>
  );
} 